import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { NutritionDetailsRow } from '@/api/reports';
import { macroDistribution } from '@/lib/nutrientDistribution';
import { LoadingState } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import { PercentBar } from './PercentBar';

/**
 * "Macronutrient distribution" — the report's 4-bar AMDR chart. Percentages are
 * the share of macro energy (Atwater 4/4/9); fat is split saturated vs
 * unsaturated by mirroring MNT's reconciliation (see nutrientDistribution.ts).
 * The four bars sum to 100% by construction, so we always render all four —
 * consistent with the PDF report.
 */
export const MacroDistributionChart = ({
  rows,
  isLoading,
}: {
  rows: NutritionDetailsRow[];
  isLoading: boolean;
}) => {
  const { t } = useTranslation();
  const { macros, hasData } = useMemo(() => macroDistribution(rows), [rows]);

  if (isLoading) return <LoadingState label={t('Loading…')} />;
  if (!hasData) {
    return (
      <EmptyState
        title={t('No data')}
        message={t('No macronutrient data logged in this date range.')}
      />
    );
  }

  return (
    <div className="space-y-3">
      {macros.map((m) => (
        <PercentBar
          key={m.key}
          label={t(m.label)}
          fraction={m.pct}
          colour={m.colour}
          amdr={m.amdr}
        />
      ))}
      <p className="pt-1 text-xs text-muted-foreground">
        {t('Data based on percent of daily energy intake per macronutrient type.')}{' '}
        <span className="font-medium">
          {t('AMDR: Protein 10-35%, Carbohydrates 45-65% and Total Fat 20-35% with Saturated Fats < 10%.')}
        </span>
      </p>
    </div>
  );
};
